import { supabase } from './supabase';

const EDGE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_EDGE_FUNCTION_URL;

// Twilio SMS Service
export const twilioService = {
  // Send SMS via Twilio Edge Function
  async sendSMS(phoneNumber, message, notificationType = 'general', relatedEntityType = null, relatedEntityId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('gym_id')
        ?.eq('id', user?.id)
        ?.single();

      // Create SMS log entry
      const { data: smsLog, error: logError } = await supabase
        ?.from('sms_logs')
        ?.insert({
          gym_id: userProfile?.gym_id,
          user_id: user?.id,
          phone_number: phoneNumber,
          message_content: message,
          notification_type: notificationType,
          status: 'pending',
          related_entity_type: relatedEntityType,
          related_entity_id: relatedEntityId
        })
        ?.select()
        ?.single();

      if (logError) throw logError;

      // Call Twilio Edge Function
      const response = await fetch(`${EDGE_FUNCTION_URL}/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: message
        })
      });

      const result = await response?.json();

      if (result?.success) {
        // Update SMS log with success
        await supabase
          ?.from('sms_logs')
          ?.update({
            status: 'sent',
            twilio_message_sid: result?.messageSid
          })
          ?.eq('id', smsLog?.id);

        return { data: { ...result, logId: smsLog?.id }, error: null };
      } else {
        // Update SMS log with failure
        await supabase
          ?.from('sms_logs')
          ?.update({
            status: 'failed',
            error_message: result?.error || 'Unknown error'
          })
          ?.eq('id', smsLog?.id);

        return { data: null, error: result?.error || 'Failed to send SMS' };
      }
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send check-in confirmation SMS
  async sendCheckInConfirmation(userId, checkInData) {
    try {
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('phone, full_name')
        ?.eq('id', userId)
        ?.single();

      if (!userProfile?.phone) {
        return { data: null, error: 'No phone number found for user' };
      }

      // Check notification preferences
      const { data: preferences } = await supabase
        ?.from('notification_preferences')
        ?.select('check_in_confirmations_enabled')
        ?.eq('user_id', userId)
        ?.single();

      if (preferences && !preferences?.check_in_confirmations_enabled) {
        return { data: null, error: 'Check-in confirmations disabled' };
      }

      let message = `Hi ${userProfile?.full_name}! You have successfully checked in at ${checkInData?.location || 'the gym'}. Have a great workout! 💪`;

      return await this.sendSMS(
        userProfile?.phone,
        message,
        'check_in_confirmation',
        'member_check_ins',
        checkInData?.checkInId
      );
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send class reminder SMS
  async sendClassReminder(userId, classData) {
    try {
      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('phone, full_name')
        ?.eq('id', userId)
        ?.single();

      if (!userProfile?.phone) {
        return { data: null, error: 'No phone number found for user' };
      }

      // Check notification preferences
      const { data: preferences } = await supabase
        ?.from('notification_preferences')
        ?.select('class_reminders_enabled, reminder_hours_before')
        ?.eq('user_id', userId)
        ?.single();

      if (preferences && !preferences?.class_reminders_enabled) {
        return { data: null, error: 'Class reminders disabled' };
      }

      let message = `Reminder: Your ${classData?.className} class with ${classData?.instructor} starts at ${classData?.time}. See you there! 🏋️`;

      return await this.sendSMS(
        userProfile?.phone,
        message,
        'class_reminder',
        'class_bookings',
        classData?.bookingId
      );
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send facility alert SMS
  async sendFacilityAlert(alertData) {
    try {
      const { data: alertConfig } = await supabase
        ?.from('alert_configurations')
        ?.select('*')
        ?.eq('alert_type', alertData?.alertType)
        ?.eq('is_active', true)
        ?.single();

      if (!alertConfig || !alertConfig?.send_sms) {
        return { data: null, error: 'Alert configuration not found or SMS disabled' };
      }

      // Get target users based on roles
      const { data: targetUsers } = await supabase
        ?.from('user_profiles')
        ?.select('id, phone, full_name')
        ?.eq('gym_id', alertData?.gymId)
        ?.in('role', alertConfig?.target_roles)
        ?.not('phone', 'is', null);

      if (!targetUsers || targetUsers?.length === 0) {
        return { data: null, error: 'No target users found' };
      }

      // Replace template variables
      let message = alertConfig?.message_template;
      Object.keys(alertData?.variables || {})?.forEach(key => {
        message = message?.replace(`{${key}}`, alertData?.variables?.[key]);
      });

      // Send SMS to all target users
      const results = await Promise.all(
        targetUsers?.map(user => 
          this.sendSMS(
            user?.phone,
            message,
            'facility_alert',
            alertData?.entityType,
            alertData?.entityId
          )
        )
      );

      return { data: results, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get SMS logs for user
  async getUserSMSLogs(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('sms_logs')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get SMS logs for gym
  async getGymSMSLogs(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('sms_logs')
        ?.select(`
          *,
          user:user_profiles!user_id(id, full_name, email)
        `)
        ?.eq('gym_id', gymId)
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.notificationType) {
        query = query?.eq('notification_type', filters?.notificationType);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get notification preferences
  async getNotificationPreferences(userId) {
    try {
      const { data, error } = await supabase
        ?.from('notification_preferences')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update notification preferences
  async updateNotificationPreferences(userId, preferences) {
    try {
      const { data, error } = await supabase
        ?.from('notification_preferences')
        ?.update(preferences)
        ?.eq('user_id', userId)
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get alert configurations
  async getAlertConfigurations(gymId) {
    try {
      const { data, error } = await supabase
        ?.from('alert_configurations')
        ?.select('*')
        ?.eq('gym_id', gymId)
        ?.order('priority', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create alert configuration
  async createAlertConfiguration(gymId, alertConfig) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('alert_configurations')
        ?.insert({
          gym_id: gymId,
          created_by: user?.id,
          ...alertConfig
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update alert configuration
  async updateAlertConfiguration(alertId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('alert_configurations')
        ?.update(updates)
        ?.eq('id', alertId)
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Schedule SMS
  async scheduleSMS(phoneNumber, message, scheduledFor, notificationType = 'general', relatedEntityType = null, relatedEntityId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        ?.from('user_profiles')
        ?.select('gym_id')
        ?.eq('id', user?.id)
        ?.single();

      const { data, error } = await supabase
        ?.from('scheduled_sms')
        ?.insert({
          gym_id: userProfile?.gym_id,
          user_id: user?.id,
          phone_number: phoneNumber,
          message_content: message,
          notification_type: notificationType,
          scheduled_for: scheduledFor,
          status: 'queued',
          related_entity_type: relatedEntityType,
          related_entity_id: relatedEntityId
        })
        ?.select()
        ?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
};